<script setup lang="ts">
  import { computed } from 'vue';
  import AppConnectivityChip from '@/components/AppConnectivityChip.vue';
  import AppIncomingAlert from '@/components/AppIncomingAlert.vue';
  import AppPrintingWidget from '@/components/AppPrintingWidget.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2AssistantTopBarButton from '@/components/v2/assistant/V2AssistantTopBarButton.vue';
  import V2StoreMonogram from '@/components/v2/store-setup/shared/V2StoreMonogram.vue';
  import { storeInitials } from '@/domain/store-setup/store-identity';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { Globals } from '@/modules/Globals';

  /** Side of the store avatar in the pill (px). */
  const AVATAR_SIZE = 26;

  /**
   * The light top bar (plan §4.2): drawer toggle, the store pill (its avatar
   * is the bar's only orange) or the brand logo, the «Asistente» pill and the
   * global status widgets. Vuetify's `v-app-bar` stays the layout host: it
   * registers the 48 px the content is padded by.
   */
  const props = defineProps<{
    restaurant: IRestaurant | null;
    printingEnabled: boolean;
  }>();

  const emit = defineEmits<{ (e: 'logout'): void }>();

  const logoUrl = computed(() =>
    props.restaurant?.logo
      ? `${Globals.uploadsPath}/${props.restaurant.logo}`
      : null
  );
  const initials = computed(() => storeInitials(props.restaurant?.name));

  /** The drawers listen to this document event (RestaurantDrawer, UserDrawer). */
  function toggleDrawer() {
    document.dispatchEvent(new CustomEvent('showRestaurantMenu'));
  }
</script>

<template>
  <!-- clipped-right: the bar stays full width over the docked «Asistente»
       panel, which registers its 400 px on the right (plan §9.3). -->
  <v-app-bar
    app
    clipped-left
    clipped-right
    flat
    dense
    height="48"
    :color="'rgb(var(--c-surface))'"
    class="v2-shell-top-bar hidden-print-only"
  >
    <button
      type="button"
      class="bar-icon-button"
      :aria-label="$t('shell.toggleMenu')"
      @click="toggleDrawer"
    >
      <PosUiIcon name="menu" />
    </button>

    <div v-if="restaurant" class="store-pill">
      <router-link :to="`/${restaurant.stringId}`" class="store-link">
        <span class="store-avatar">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="store-logo" />
          <V2StoreMonogram
            v-else
            :initials="initials"
            :size="AVATAR_SIZE"
            tone="brand"
          />
        </span>
        <span class="store-name">{{ restaurant.name }}</span>
      </router-link>
      <router-link
        to="/dashboard"
        class="store-exit"
        :aria-label="$t('shell.leaveStore')"
        :title="$t('shell.leaveStore')"
      >
        <PosUiIcon name="close" />
      </router-link>
    </div>
    <router-link v-else to="/" class="home-link" :aria-label="$t('shell.home')">
      <img
        class="home-logo"
        :src="'assets/img/logos/logoorange@25.png'"
        alt="QueRestó"
      />
    </router-link>

    <div class="bar-spacer" />

    <V2AssistantTopBarButton />
    <AppConnectivityChip class="bar-widget bar-connectivity" />
    <AppIncomingAlert class="bar-widget" />
    <!-- Same visibility gate as the old printing button. -->
    <AppPrintingWidget v-if="printingEnabled" class="bar-widget" />
    <button
      type="button"
      class="bar-icon-button"
      :aria-label="$t('shell.logout')"
      :title="$t('shell.logout')"
      @click="emit('logout')"
    >
      <PosUiIcon name="logout" />
    </button>
  </v-app-bar>
</template>

<style scoped lang="scss">
  .v2-shell-top-bar {
    // Hairline inside the 48 px Vuetify registers (a border would add 1 px).
    box-shadow: inset 0 -1px 0 rgb(var(--c-neutral-200)) !important;

    :deep(.v-toolbar__content) {
      @apply tw-px-1;
    }

    .bar-icon-button {
      @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-2xl tw-text-neutral-600 tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-bg-neutral-100;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .store-pill {
      @apply tw-ml-1 tw-flex tw-h-[34px] tw-min-w-0 tw-items-center tw-rounded-full tw-bg-neutral-100 tw-pl-1 tw-text-neutral-900;
    }

    .store-link {
      @apply tw-flex tw-h-full tw-min-w-0 tw-items-center tw-rounded-full tw-pr-1 tw-text-neutral-900 tw-no-underline tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-bg-neutral-200;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .store-avatar {
      @apply tw-mr-2 tw-flex tw-h-[26px] tw-w-[26px] tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-surface;
    }

    .store-logo {
      @apply tw-h-full tw-w-full tw-object-cover;
    }

    .store-name {
      @apply tw-min-w-0 tw-truncate tw-text-sm tw-font-medium;

      // Phones: the name, not the whole pill, gives way to the bar's buttons.
      @media (max-width: 599px) {
        max-width: 40vw;
      }
    }

    .store-exit {
      @apply tw-ml-1 tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-lg tw-text-neutral-500 tw-no-underline;

      &:hover {
        @apply tw-bg-neutral-200 tw-text-neutral-800;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .home-link {
      @apply tw-ml-1 tw-flex tw-h-11 tw-items-center tw-rounded-lg tw-px-1;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .home-logo {
      @apply tw-h-7 tw-w-auto;
    }

    .bar-spacer {
      @apply tw-flex-1;
    }

    .bar-widget {
      @apply tw-mr-1;
    }

    // Phones: the always-green «online» dot makes room for the store name;
    // it comes back as soon as there is something to say.
    .bar-connectivity.is-idle {
      @media (max-width: 599px) {
        display: none;
      }
    }
  }
</style>
